import React from 'react';

import FormContainer from 'components/LoanForm/FormContainer';
import FormNavigation from 'components/FormNavigation';
import { FlowComponentType } from 'routes/types';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useCurrentFlow from 'hooks/useCurrentFlow';
import { authOtp, sendEmailCode } from 'thunks';
import { useDispatch, useSelector } from 'react-redux';
import { getAuthData } from 'selectors/getAuthData';
import { getYourContactData } from 'selectors/getYourContact';
import { getYourNameData } from 'selectors/yourName';
import { getAuthOtpBorrowerFields } from 'handlers/yourName';
import { useResendLogic } from 'hooks/useResendLogic';
import { ContactType } from 'handlers/yourContact';
import { VerifyEmailResult } from 'enums/FlowNextResults';
import VerifyCode from 'components/VerifyCode';
import { ContinueMode } from 'components/VerifyCode/VerifyCode';
import { setYourIncomeVariant, YourIncomeVariant } from 'handlers/yourIncome';
import ResendConfirmationModal from 'components/ResendConfirmationModal';
import { useResendConfirmationModal } from 'hooks/useResendConfirmationModal';

const VerifyEmail = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { isCardFlow } = useCurrentFlow();
  const { canResend, getFormattedTime } = useResendLogic(ContactType.Email);

  const { emailId } = useSelector(getAuthData);
  const { phone_number: phoneNumber, email } = useSelector(getYourContactData);
  const yourNameData = useSelector(getYourNameData);
  const borrowerFields = getAuthOtpBorrowerFields(yourNameData);

  const performEmailResend = async () => {
    await dispatchWithUnwrap(sendEmailCode({ phoneNumber, email }));
    recordSent(email);
  };

  const { onResendCode, resendConfirmationModalProps, recordSent } = useResendConfirmationModal({
    contactType: ContactType.Email,
    contactValue: email,
    performResend: performEmailResend,
  });

  const onVerify = async (code: string) => {
    const authResponse = await dispatchWithUnwrap(
      authOtp({ code, phoneOrEmailId: emailId!, email, ...borrowerFields }),
    );
    if (authResponse.verified) {
      if (isCardFlow) dispatch(setYourIncomeVariant(YourIncomeVariant.CardWaitlist));
      handleNext(isCardFlow ? VerifyEmailResult.CardFlow : undefined);
    }
    return authResponse.verified;
  };

  const resendButtonProps = {
    disabled: !canResend,
    ...(!canResend && { text: `Resend code (${getFormattedTime()})` }),
  };

  return (
    <>
      <ResendConfirmationModal {...resendConfirmationModalProps} />

      <FormNavigation {...navigationInfo} {...(isCardFlow && { step: 5, title: 'Sign Up' })} />
      <FormContainer title="Verify email" subtitle={`Enter the 6-digit authorization code sent to ${email}`}>
        <VerifyCode
          onResendCode={onResendCode}
          onVerify={onVerify}
          handleSessionError={handleNext}
          continueMode={ContinueMode.OnCodeValid}
          noAutoComplete
          resendButtonProps={resendButtonProps}
        />
      </FormContainer>
    </>
  );
};

export default VerifyEmail;
